import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchTechnicalAlerts } from '../data/alertsApi';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for managing technical alerts table data with server-side filtering
 * 
 * @param {Object} filters - Current active filters (filteredInfo object)
 * @param {Object} options - Additional options (pageSize, initialPage, etc.)
 * @returns {Object} - { data, loading, error, pagination, setPage, refetch }
 */
const useTechnicalAlertsTableData = (filters = {}, options = {}) => {
  const { 
    pageSize = 10,
    initialPage = 1,
    filterData = null,
    sorter = null
  } = options;
  
  const { currentUser } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState(null);
  const [allAlertIds, setAllAlertIds] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: initialPage,
    totalPages: 1,
    totalCount: 0,
    pageSize: pageSize
  });

  // Use ref to track the latest filters to avoid stale closures
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Id of the newest request: fetches start immediately, so quick filter/page changes overlap, and a
  // slower older response must not overwrite the newer one.
  const latestRequest = useRef(0);

  /**
   * Fetch table data from the API
   */
  const fetchTableData = useCallback(async (page = pagination.currentPage, immediateFilters = null) => {
    const requestId = ++latestRequest.current;
    const clientId = currentUser?.customer_id;
    
    if (!clientId) {
      setData([]);
      setTotals(null);
      setAllAlertIds([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the provided filters or fall back to current filters
      const currentFilters = immediateFilters || filtersRef.current;
      
      // Map frontend filter keys to backend expected format
      const mappedFilters = {};
      
      Object.entries(currentFilters).forEach(([key, values]) => {
        if (values && Array.isArray(values) && values.length > 0) {
          if (key === 'actions') {
            mappedFilters.status = values;
          } else if (key === 'route') {
            mappedFilters.routes = values;
          } else {
            mappedFilters[key] = values;
          }
        }
      });
      

      // Prepare sort parameters
      const sortParams = {};
      if (sorter && sorter.columnKey && sorter.order) {
        sortParams.column = sorter.columnKey;
        sortParams.order = sorter.order === 'ascend' ? 'asc' : 'desc';
      }

      const result = await fetchTechnicalAlerts(
        clientId,
        mappedFilters, 
        sortParams, 
        page, 
        pagination.pageSize
      );
      if (requestId !== latestRequest.current) return;
      
      setData(result.data || []);
      setTotals(result.totals || null);
      
      // Extract all alert IDs from the response  
      // The backend provides allAlertIds which contains ALL IDs across all pages matching current filters
      setAllAlertIds(result.allAlertIds || []);
      
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: result.pagination.totalPages,
        totalCount: result.pagination.totalCount
      }));
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      setError(err);
      // Set empty data on error to allow graceful degradation
      setData([]);
      setTotals(null);
      setAllAlertIds([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalCount: 0
      }));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [pagination.pageSize, sorter, currentUser?.customer_id]);

  /**
   * Set current page and fetch data
   */
  const setPage = useCallback((page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchTableData(page);
  }, [fetchTableData]);

  /**
   * Refetch data with current filters and pagination
   */
  const refetch = useCallback((immediateFilters = null) => {
    // Reset to page 1 when filters change
    const targetPage = immediateFilters ? 1 : pagination.currentPage;
    if (immediateFilters) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
    fetchTableData(targetPage, immediateFilters);
  }, [fetchTableData, pagination.currentPage]);

  /**
   * Refetch for filter/sort changes - always resets to page 1
   */
  const refetchForFilters = useCallback(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchTableData(1);
  }, [fetchTableData]);

  // Effect to handle initial load and filter/sort changes (NOT pagination)
  // Only a real filter/sort change resets to page 1; a re-run with the same query (mount, StrictMode
  // double-invoke, client loaded) keeps the current page, so a restored view keeps its page.
  const lastQueryRef = useRef(null);
  useEffect(() => {
    const last = lastQueryRef.current;
    lastQueryRef.current = { filters, sorter };
    if (last && (last.filters !== filters || last.sorter !== sorter)) {
      refetchForFilters();
    } else {
      fetchTableData(pagination.currentPage);
    }
  }, [filters, sorter, refetchForFilters]);

  return {
    data,
    loading,
    error,
    totals,
    pagination,
    setPage,
    refetch,
    allAlertIds
  };
};

export default useTechnicalAlertsTableData;
